import { useCallback, useEffect } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";

import { updateSearchResultNavigationStore } from "../../../../util/searchResultNavigation";
import {
  artworkFilterStore,
  artworkNavigationStore,
} from "../../components/useArtworkFilters";
import { buildArtworkNavigationTargetUrl } from "../../components/searchResultNavigation";

import HeaderComponent from "../../../../components/ui/ListHeader";
import Actions from "./components/Actions";
import * as ImageSelectListSaved from "./components/ImageSelectListSaved";
import * as ImageSelectListWorking from "./components/ImageSelectListWorking";
import searchedArtworks from "./components/SearchedArtworks";
import * as SelectListSaved from "./components/SelectListSaved";
import * as SelectListWorking from "./components/SelectListWorking";

import {
  multiSelect,
  SelectList,
  Table,
} from "../../../../components/Functional";
import {
  FETCH_LISTS as fetchLists,
  FETCH_LIST as fetchList,
} from "../../../../graphql/query/List";
import { CREATE_LIST as createListMutation } from "../../../../graphql/mutation/List";
import { LIST_TYPES } from "../../../../constants/listTypes";

const List = props => {
  const { formik } = props;
  const navigate = useNavigate();
  const {
    data: { lists: { edges = [] } = {} } = {},
    loading: loadingWorkingList,
  } = useQuery(fetchLists(), {
    variables: {
      input: { typeId: LIST_TYPES.ARTWORK_WORKING },
    },
    fetchPolicy: "network-only",
  });
  const {
    data: { lists: { edges: imageListEdges = [] } = {} } = {},
    loading: loadingImageWorkingList,
  } = useQuery(fetchLists(), {
    variables: {
      input: { typeId: LIST_TYPES.ARTWORK_IMAGE_WORKING },
    },
    fetchPolicy: "network-only",
  });

  const [createList] = useMutation(createListMutation(), {
    refetchQueries: [
      {
        query: fetchLists(),
        variables: { input: {} },
        fetchPolicy: "network-only",
      },
    ],
    awaitRefetchQueries: true,
  });

  // Get the first list's ID if it exists (even if empty, the list object will have an id)
  const workingListId = edges?.[0]?.id ?? null;
  const imageWorkingListId = imageListEdges?.[0]?.id ?? null;

  // Ensure working lists exist - create them if they don't
  useEffect(() => {
    if (!loadingWorkingList && !workingListId) {
      createList({
        variables: {
          input: {
            typeId: LIST_TYPES.ARTWORK_WORKING,
            name: `select_list_working_${Date.now()}`,
          },
        },
      });
    }
  }, [loadingWorkingList, workingListId, createList]);

  useEffect(() => {
    if (!loadingImageWorkingList && !imageWorkingListId) {
      createList({
        variables: {
          input: {
            typeId: LIST_TYPES.ARTWORK_IMAGE_WORKING,
            name: `select_list_working_${Date.now()}`,
          },
        },
      });
    }
  }, [loadingImageWorkingList, imageWorkingListId, createList]);
  const queryParams = new URLSearchParams(window.location.search);
  const status = queryParams?.get("status") || "all";
  const listIdParam = queryParams.get("listId");
  const imageListIdParam = queryParams.get("imageListId");
  const workingListIdParam = queryParams.get("workingListId");
  const imageWorkingListIdParam = queryParams.get("imageWorkingListId");
  const listId = listIdParam ? +listIdParam : null;
  const imageListId = imageListIdParam ? +imageListIdParam : null;

  // Determine if listId/imageListId are working lists or saved lists
  const isWorkingList = listId && listId === workingListId;
  const isImageWorkingList = imageListId && imageListId === imageWorkingListId;

  // Fetch saved list names when viewing saved lists (not working lists)
  const { data: { list: savedList } = {}, loading: loadingSavedList } =
    useQuery(fetchList(), {
      variables: { id: listId },
      skip: !listId || isWorkingList,
    });

  const {
    data: { list: savedImageList } = {},
    loading: loadingSavedImageList,
  } = useQuery(fetchList(), {
    variables: { id: imageListId },
    skip: !imageListId || isImageWorkingList,
  });
  const ids = Object.keys(formik?.values?.selected?.ids || {})?.map(item =>
    parseInt(item.replace("_", "")),
  );
  const select = formik?.values?.selected?.select;

  useEffect(() => {
    const currentParams = new URLSearchParams(window.location.search);
    let shouldUpdate = false;

    // Remove workingListId if we're viewing a saved list (listId is present)
    if (listId && currentParams.has("workingListId")) {
      currentParams.delete("workingListId");
      shouldUpdate = true;
    }

    // Remove imageWorkingListId if we're viewing a saved image list (imageListId is present)
    if (imageListId && currentParams.has("imageWorkingListId")) {
      currentParams.delete("imageWorkingListId");
      shouldUpdate = true;
    }

    // Update workingListId if we're on selectListWorking tab, it's available, and no saved listId
    if (status === "selectListWorking" && workingListId && !listId) {
      if (currentParams.get("workingListId") !== String(workingListId)) {
        currentParams.set("workingListId", workingListId);
        shouldUpdate = true;
      }
    }

    // Update imageWorkingListId if we're on imageSelectListWorking tab, it's available, and no saved imageListId
    if (
      status === "imageSelectListWorking" &&
      imageWorkingListId &&
      !imageListId
    ) {
      if (
        currentParams.get("imageWorkingListId") !== String(imageWorkingListId)
      ) {
        currentParams.set("imageWorkingListId", imageWorkingListId);
        shouldUpdate = true;
      }
    }

    if (shouldUpdate) {
      const newUrl = `${window.location.pathname}?${currentParams.toString()}`;
      // eslint-disable-next-line
      history.pushState(null, null, newUrl);
    }
  }, [workingListId, imageWorkingListId, status, listId, imageListId]);

  const multiSelectProps = {
    formik,
    label: "Artworks",
    name: "selected",
    keyName: "id",
  };

  const actionProps = {
    formik,
    ids,
    imageListId,
    imageWorkingListId,
    listId,
    workingListId,
    onSuccess: () => formik?.resetForm(),
    search: artworkFilterStore.get(),
  };

  const captureNavigationSnapshot = useCallback(
    ({ count, edges, queryVariables }) => {
      if (status !== "all") return;

      updateSearchResultNavigationStore(artworkNavigationStore, {
        count,
        edges,
        queryVariables,
        statusContext: "all",
        listContext: {
          ...(listIdParam ? { listId: listIdParam } : {}),
          ...(imageListIdParam ? { imageListId: imageListIdParam } : {}),
          ...(workingListIdParam ? { workingListId: workingListIdParam } : {}),
          ...(imageWorkingListIdParam
            ? { imageWorkingListId: imageWorkingListIdParam }
            : {}),
        },
      });
    },
    [
      imageListIdParam,
      imageWorkingListIdParam,
      listIdParam,
      status,
      workingListIdParam,
    ],
  );

  const handleChange = value => {
    formik?.resetForm();
    let url = `/artworks?status=${value}`;

    // For working list tabs, use listId if present (saved list), otherwise use workingListId
    if (value === "selectListWorking" || value === "selectListSaved") {
      // Include listId if present (viewing/editing a saved list), otherwise use workingListId
      if (listId) {
        url += `&listId=${listId}`;
      } else if (workingListId) {
        url += `&workingListId=${workingListId}`;
      }
      // Preserve the current image list context when switching to select list tabs
      if (imageListId) {
        url += `&imageListId=${imageListId}`;
      } else if (imageWorkingListId) {
        url += `&imageWorkingListId=${imageWorkingListId}`;
      }
    } else if (
      value === "imageSelectListWorking" ||
      value === "imageSelectListSaved"
    ) {
      // Include imageListId if present (viewing/editing a saved image list), otherwise use imageWorkingListId
      if (imageListId) {
        url += `&imageListId=${imageListId}`;
      } else if (imageWorkingListId) {
        url += `&imageWorkingListId=${imageWorkingListId}`;
      }
      // Preserve the current artwork list context when switching to image list tabs
      if (listId) {
        url += `&listId=${listId}`;
      } else if (workingListId) {
        url += `&workingListId=${workingListId}`;
      }
    } else {
      // For "all" and other tabs, preserve saved list IDs (listId/imageListId) so items can be added to them
      // But don't include working list IDs
      if (listId) {
        url += `&listId=${listId}`;
      }
      if (imageListId) {
        url += `&imageListId=${imageListId}`;
      }
    }

    navigate(url);
  };

  const GeneralTabActions = () => {
    // Determine target list IDs - use saved list IDs if present, otherwise use working list IDs
    const targetListId =
      listId && !isNaN(listId) ? listId : workingListId || undefined;
    const targetImageListId =
      imageListId && !isNaN(imageListId)
        ? imageListId
        : imageWorkingListId || undefined;

    const createListProps = {
      ...actionProps,
      // Use listId if present (saved list), otherwise use workingListId
      ...(targetListId ? { targetListId } : {}),
      onSuccess: () => {
        formik?.resetForm();
        navigate(
          `/artworks?status=selectListWorking${listId ? `&listId=${listId}` : ""}${imageListId ? `&imageListId=${imageListId}` : ""}`,
        );
      },
      typeId: LIST_TYPES.ARTWORK_WORKING,
    };

    const createImageListProps = {
      ...actionProps,
      // Use imageListId if present (saved image list), otherwise use imageWorkingListId
      ...(targetImageListId ? { targetListId: targetImageListId } : {}),
      label: "Add Images to Select List",
      onSuccess: () => {
        formik?.resetForm();
        navigate(
          `/artworks?status=imageSelectListWorking${listId ? `&listId=${listId}` : ""}${imageListId ? `&imageListId=${imageListId}` : ""}`,
        );
      },
      typeId: LIST_TYPES.ARTWORK_IMAGE_WORKING,
      isSearch: true,
      ...(select !== "all"
        ? { search: { id: { operator: "in", value: ids } } }
        : {}),
    };

    const createPrimaryImageListProps = {
      ...actionProps,
      // Use imageListId if present (saved image list), otherwise use imageWorkingListId
      ...(targetImageListId ? { targetListId: targetImageListId } : {}),
      label: "Add Primary Images to Select List",
      onSuccess: () => {
        formik?.resetForm();
        navigate(
          `/artworks?status=imageSelectListWorking${listId ? `&listId=${listId}` : ""}${imageListId ? `&imageListId=${imageListId}` : ""}`,
        );
      },
      typeId: LIST_TYPES.ARTWORK_IMAGE_WORKING,
      primary: true,
      isSearch: true,
      ...(select !== "all"
        ? { search: { id: { operator: "in", value: ids } } }
        : {}),
    };

    return (
      <>
        <SelectList.CreateList {...createPrimaryImageListProps} />
        <SelectList.CreateList {...createImageListProps} />
        <SelectList.CreateList {...createListProps} />
      </>
    );
  };

  const tabProps = {
    active: status,
    onChange: handleChange,
    tabs: [
      {
        actions: GeneralTabActions,
        label: "All Artworks",
        name: "all",
        tableProps: searchedArtworks(),
      },
      {
        actions: SelectListWorking.Actions,
        label:
          listId && !isWorkingList
            ? loadingSavedList
              ? "Select List (...)"
              : savedList?.name
                ? `Select List (${savedList.name})`
                : "Select List (Working)"
            : "Select List (Working)",
        name: "selectListWorking",
        tableProps: SelectListWorking.tableProps(actionProps),
      },
      {
        actions: SelectListSaved.Actions,
        label: "Select List (Saved)",
        name: "selectListSaved",
        tableProps: SelectListSaved.tableProps({ formik, listId }),
      },
      {
        actions: ImageSelectListWorking.Actions,
        label:
          imageListId && !isImageWorkingList
            ? loadingSavedImageList
              ? "Image Select List (...)"
              : savedImageList?.name
                ? `Image Select List (${savedImageList.name})`
                : "Image Select List (Working)"
            : "Image Select List (Working)",
        name: "imageSelectListWorking",
        tableProps: ImageSelectListWorking.tableProps({
          imageListId,
          imageWorkingListId,
          formik,
        }),
      },
      {
        actions: ImageSelectListSaved.Actions,
        label: "Image Select List (Saved)",
        name: "imageSelectListSaved",
        tableProps: ImageSelectListSaved.tableProps({ formik, imageListId }),
      },
    ],
  };
  const { tableProps: tabTableProps, actions: tabActions } =
    tabProps?.tabs?.find(tab => tab.name === status) || {};
  const TabActions = tabActions;

  const fixHeadersName = ({ status, headers }) =>
    headers?.map(header => {
      return {
        ...header,
        name:
          header?.name && status === "selectListWorking"
            ? Array.isArray(header?.name)
              ? header?.name?.map(item => `item.${item}`)
              : `item.${header?.name}`
            : header?.name,
      };
    });

  const tableProps = {
    dataKey: "artworks",
    headers: fixHeadersName({
      status,
      headers: [
        multiSelect(multiSelectProps),
        {
          label: "Image",
          name: "imagesSummary.imgT",
          type: "image",
        },
        {
          label: "Artist",
          name: "artists",
          type: "custom",
          component: props => (
            <div
              className={`overflow-hidden text-ellipsis text-base font-medium leading-5 text-black`}
              style={{ maxWidth: 200 }}
            >
              {(
                (status === "selectListWorking"
                  ? props?.values?.item?.artists
                  : props?.values?.artists
                )?.map(item => item?.name) || []
              )?.join(", ")}
            </div>
          ),
        },
        {
          label: "Title",
          name: "title",
          type: "link",
          to: ({ values }) => {
            const artworkId = values?.item?.id ?? values?.id;
            if (artworkId == null) {
              return "";
            }
            return buildArtworkNavigationTargetUrl({
              listContext: {
                ...(listIdParam ? { listId: listIdParam } : {}),
                ...(imageListIdParam ? { imageListId: imageListIdParam } : {}),
                ...(workingListIdParam
                  ? { workingListId: workingListIdParam }
                  : {}),
                ...(imageWorkingListIdParam
                  ? { imageWorkingListId: imageWorkingListIdParam }
                  : {}),
              },
              recordId: artworkId,
              status,
            });
          },
        },
        { label: "Description", name: "description", type: "label" },
        { label: "Medium", name: "medium", type: "label" },
        { label: "Dimensions", name: "dimensions", type: "label" },
        { label: "", type: "custom", component: Actions },
      ],
    }),
    inputs: [],
    onQueryStateChange: captureNavigationSnapshot,
    tabProps,
    variant: "dynamicHeight",
    ...tabTableProps,
  };

  return (
    <>
      <HeaderComponent
        leftText="All Artworks"
        rightComponent={() => {
          return (
            <div className="flex flex-wrap gap-2 sm:gap-4">
              {TabActions ? <TabActions {...actionProps} /> : null}
            </div>
          );
        }}
      />
      <div className="mt-6 w-full">
        <Table {...tableProps} />
      </div>
    </>
  );
};

export default List;
